import { applyMiddleware, combineReducers, createStore } from '@reduxjs/toolkit'
import { TypedUseSelectorHook, useDispatch as useReduxDispatch, useSelector as useReduxSelector } from 'react-redux'
import { profileReducer } from './profileReducer'
import { uiReducer } from './uiReducer'
import { persistStore, persistReducer, createTransform } from 'redux-persist'
import persistStorage from 'redux-persist/lib/storage'
import api from '../api'
import { composeWithDevTools } from 'redux-devtools-extension'
import thunk from 'redux-thunk'

// Transform to exclude isAuth from persistence
// This ensures we always check Django session on app load
// But we keep user data (id, email, etc.) so we can restore it if session is valid
const profileTransform = createTransform(
  // transform state on its way to being serialized and persisted
  (inboundState: any) => {
    if (!inboundState) return inboundState
    const { isAuth, ...rest } = inboundState
    return rest
  },
  // transform state being rehydrated
  (outboundState: any) => {
    // Keep user data but reset isAuth - session check will set it if valid
    return { ...outboundState, isAuth: false }
  },
  { whitelist: ['profile'] },
)

const persistConfig = {
  key: 'root',
  storage: persistStorage,
  whitelist: ['profile', 'ui'],
  transforms: [profileTransform],
  debug: process.env.NODE_ENV === 'development',
}

export const rootReducer = combineReducers({
  [api.reducerPath]: api.reducer,
  profile: profileReducer,
  ui: uiReducer,
})

export type RootState = ReturnType<typeof rootReducer>

const persistedReducer = persistReducer<RootState>(persistConfig, rootReducer)

export const store = createStore(persistedReducer, composeWithDevTools(applyMiddleware(thunk, api.middleware)))

export const useSelector: TypedUseSelectorHook<RootState> = useReduxSelector

export type AppDispatch = typeof store.dispatch

export const useDispatch = () => useReduxDispatch<AppDispatch>()

export const persistor = persistStore(store)
